import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUp, CalendarClock, CircleDollarSign, Info, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { CampoDeData } from '@/componentes/CampoDeData';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela } from '@/componentes/JanelaDeAcao';
import { Seletor } from '@/componentes/Seletor';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarCompetencia, formatarDia, formatarReais, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDoOrcamento, type Caixa } from './api';
import { detalheDoSaldo, NOME_DA_ORIGEM_DO_EVENTO, reaisComSinal } from './orcamento';
import type { AcaoDoCabecalho } from './PaginaOrcamento';

type Horizonte = '3' | '6' | '12';
type Evento = Caixa['eventos'][number];

/**
 * FERRAMENTA 4 DO MÓDULO 19 — a necessidade de caixa: o menor saldo do
 * horizonte e a data, a partir do saldo do banco informado à mão (não há
 * integração bancária) e do que a operação já conhece. O que só o ERP terá
 * fica escrito, e o que foi tirado para não contar duas vezes também.
 */
export function CaixaDoOrcamento({ acao, aoFecharAcao }: { readonly acao: AcaoDoCabecalho; readonly aoFecharAcao: () => void }) {
  const [horizonte, setHorizonte] = useState<Horizonte>('6');
  const consulta = useQuery({ queryKey: ['orcamento', 'caixa', horizonte], queryFn: () => apiDoOrcamento.caixa(Number(horizonte)), meta: { oQueLia: 'a projeção de caixa' } });
  const [pagina, setPagina] = useState(1);
  const c = consulta.data;
  const porPagina = 20;
  const eventos = c?.eventos ?? [];
  const paginas = Math.max(1, Math.ceil(eventos.length / porPagina));
  const atual = Math.min(pagina, paginas);
  const saldo = c?.saldos[0] ?? null;

  const colunasDosEventos: readonly Coluna<Evento>[] = [
    { chave: 'data', rotulo: 'Data', quebra: 'nunca', celula: (e) => <span className="numero whitespace-nowrap text-tinta">{formatarDia(e.data)}</span> },
    {
      chave: 'movimento',
      rotulo: 'Movimento',
      prioridade: 'chave',
      celula: (e) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{e.referencia}</span>
          <TextoDaCelula texto={`${NOME_DA_ORIGEM_DO_EVENTO[e.origem]}${e.atrasado ? ' · já devia ter acontecido' : ''}`} classe={`txt-apoio ${e.atrasado ? 'text-atencao-texto' : 'text-tinta-apagada'}`} linhas={1} />
        </span>
      ),
    },
    { chave: 'entrada', rotulo: 'Entrada', alinhamento: 'numero', celula: (e) => (e.tipo === 'entrada' ? <span className="numero whitespace-nowrap text-tinta">{formatarReais(e.valor)}</span> : <span className="txt-apoio text-tinta-apagada">—</span>) },
    { chave: 'saida', rotulo: 'Saída', alinhamento: 'numero', celula: (e) => (e.tipo === 'saida' ? <span className="numero whitespace-nowrap text-tinta">{formatarReais(e.valor)}</span> : <span className="txt-apoio text-tinta-apagada">—</span>) },
    { chave: 'saldo', rotulo: 'Saldo depois', alinhamento: 'numero', celula: (e) => <span className={`numero whitespace-nowrap ${e.saldoDepois < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(e.saldoDepois)}</span> },
  ];

  return (
    <>
      <div className="flex flex-wrap items-end gap-4">
        <Seletor<Horizonte>
          rotulo="Horizonte"
          valor={horizonte}
          aoEscolher={(h) => { setHorizonte(h); setPagina(1); }}
          opcoes={[{ valor: '3', rotulo: '3 meses' }, { valor: '6', rotulo: '6 meses' }, { valor: '12', rotulo: '12 meses' }]}
        />
        <p className="txt-apoio min-w-0 flex-1 text-pretty text-tinta-secundaria">
          {c ? `De ${formatarDia(c.dataBase)} a ${formatarDia(c.fim)}${c.versao ? `, com o orçamento ${String(c.versao.exercicio)} (versão ${String(c.versao.versao)})` : ', sem orçamento aprovado'}.` : 'A projeção parte do saldo informado.'}
        </p>
      </div>
      <div className="bloco-rebaixado flex items-start gap-3 rounded-controle p-4">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-informacao" />
        {c ? <p className="txt-corpo text-pretty text-tinta">{c.frase}</p> : (
          <Carregando oQue="Carregando a frase do caixa">
            <Barra className="my-1 h-3 w-96 max-w-full" />
          </Carregando>
        )}
      </div>

      {consulta.isError && !c ? (
        <ErroDaLista oQue="A projeção de caixa" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : (
        <>
          <FaixaIndicadores>
            <CardIndicador
              rotulo="Saldo informado"
              icone={CircleDollarSign}
              tom="neutro"
              carregando={!c}
              valor={c ? (saldo ? reaisComSinal(saldo.valor) : 'Sem saldo') : ''}
              alerta={(saldo?.valor ?? 0) < 0}
              contexto={saldo ? `${formatarDia(saldo.data)} · ${saldo.fonte}` : 'Informe o saldo do extrato'}
            />
            <CardIndicador
              rotulo="Menor saldo do horizonte"
              icone={TriangleAlert}
              tom="parada"
              carregando={!c}
              valor={c ? reaisComSinal(c.menor.valor) : ''}
              alerta={(c?.menor.valor ?? 0) < 0}
              contexto={c ? (c.saldoInformado ? 'A partir do saldo informado' : 'Sem saldo: só a variação, a partir de zero') : 'Menor saldo'}
            />
            <CardIndicador
              rotulo="Data do menor saldo"
              icone={CalendarClock}
              tom="standby"
              carregando={!c}
              valor={c ? formatarDia(c.menor.data) : ''}
              contexto="Quando o caixa fica mais apertado"
            />
            <CardIndicador
              rotulo="Entradas e saídas previstas"
              icone={c && c.totais.entradas >= c.totais.saidas ? ArrowUp : ArrowDownLeft}
              tom="disponivel"
              carregando={!c}
              valor={c ? reaisComSinal(c.totais.entradas - c.totais.saidas) : ''}
              contexto={c ? `Entram ${formatarReais(c.totais.entradas)} · saem ${formatarReais(c.totais.saidas)}` : 'No horizonte'}
            />
          </FaixaIndicadores>

          <h2 className="txt-secao text-tinta">Como o saldo anda mês a mês?</h2>
          <Tabela
            rotulo="O caixa mês a mês"
            colunas={[
              { chave: 'competencia', rotulo: 'Mês', prioridade: 'chave', celula: (m) => <span className="codigo text-tinta">{formatarCompetencia(m.competencia)}</span> },
              { chave: 'entradas', rotulo: 'Entradas', alinhamento: 'numero', celula: (m) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(m.entradas)}</span> },
              { chave: 'saidas', rotulo: 'Saídas', alinhamento: 'numero', celula: (m) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(m.saidas)}</span> },
              { chave: 'saldo', rotulo: 'Saldo no fim', alinhamento: 'numero', celula: (m) => <span className={`numero whitespace-nowrap ${m.saldoFinal < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(m.saldoFinal)}</span> },
            ]}
            linhas={c?.porMes ?? []}
            chaveDaLinha={(m) => m.competencia}
            carregando={consulta.isPending}
            erro={consulta.isError ? <ErroDaLista oQue="A projeção de caixa" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
            vazio={<EstadoVazio titulo="Nenhum mês no horizonte" descricao="Escolha o horizonte de 3, 6 ou 12 meses." />}
            rodape={<RodapeDeContagem total={c?.porMes.length ?? 0} />}
          />

          <h2 className="txt-secao text-tinta">O que entra e o que sai, em ordem</h2>
          <Tabela
            rotulo="Os movimentos previstos"
            colunas={colunasDosEventos}
            linhas={eventos.slice((atual - 1) * porPagina, atual * porPagina)}
            chaveDaLinha={(e) => `${e.data}-${e.origem}-${e.referencia}-${String(e.valor)}`}
            carregando={consulta.isPending}
            erro={consulta.isError ? <ErroDaLista oQue="A projeção de caixa" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
            alturaDupla
            vazio={
              <EstadoVazio
                titulo="Nenhum movimento previsto"
                descricao="Sem documento a receber, medição sem documento, fatura de posto, pedido em aberto nem orçamento aprovado no horizonte. O saldo fica como o informado."
              />
            }
            rodape={
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <RodapeDeContagem total={eventos.length} />
                {paginas > 1 ? (
                  <span className="flex gap-2">
                    <Botao nivel="fantasma" tamanho="p" disabled={atual <= 1} onClick={() => { setPagina(atual - 1); }}>Página anterior</Botao>
                    <Botao nivel="fantasma" tamanho="p" disabled={atual >= paginas} onClick={() => { setPagina(atual + 1); }}>Próxima página</Botao>
                  </span>
                ) : null}
              </div>
            }
          />

          {c ? (
            <section className="cartao flex flex-col gap-4 p-6" aria-label="O que não entrou e o que não contou duas vezes">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <h2 className="txt-secao text-tinta">O mesmo fato não entrou duas vezes</h2>
                  <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
                    <li>{`${String(c.deduplicados.medicoesComDocumento)} ${c.deduplicados.medicoesComDocumento === 1 ? 'medição já faturada entrou' : 'medições já faturadas entraram'} só pelo documento`}</li>
                    <li>{`${formatarReais(c.deduplicados.pedidoAbatidoPorTitulo)} de pedido ${c.deduplicados.pedidoAbatidoPorTitulo === 0 ? 'abatido' : 'abatidos'} pela parte que já virou título`}</li>
                    <li>{`${String(c.deduplicados.titulosJaPagos)} ${c.deduplicados.titulosJaPagos === 1 ? 'título venceu' : 'títulos venceram'} antes da data-base e ${c.deduplicados.titulosJaPagos === 1 ? 'está' : 'estão'} no saldo`}</li>
                    {(c.deduplicados.recebimentoAbatidoPeloErp ?? 0) > 0 ? <li>{`${formatarReais(c.deduplicados.recebimentoAbatidoPeloErp ?? 0)} de recebimento de pedido representados pelo título do ERP do mesmo pedido`}</li> : null}
                    {(c.deduplicados.cobrancaNoTituloDoErp ?? 0) > 0 ? <li>{`${String(c.deduplicados.cobrancaNoTituloDoErp ?? 0)} ${c.deduplicados.cobrancaNoTituloDoErp === 1 ? 'cobrança ou medição entrou' : 'cobranças ou medições entraram'} só pelo título a receber`}</li> : null}
                    {(c.deduplicados.faturaDePostoNoTituloDoErp ?? 0) > 0 ? <li>{`${String(c.deduplicados.faturaDePostoNoTituloDoErp ?? 0)} ${c.deduplicados.faturaDePostoNoTituloDoErp === 1 ? 'fatura de posto entrou' : 'faturas de posto entraram'} só pelo título do ERP`}</li> : null}
                    <li>{`${formatarReais(c.deduplicados.custoOrcadoCoberto)} de custo orçado já ${c.deduplicados.custoOrcadoCoberto === 0 ? 'coberto' : 'cobertos'} pelo realizado e pelo comprometido`}</li>
                  </ul>
                </div>
                <div className="flex flex-col gap-2">
                  <h2 className="txt-secao text-tinta">{c.completo ? 'Nada ficou fora' : 'O que ficou fora'}</h2>
                  <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
                    {c.naoEntrou.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              </div>
              {c.saldos.length > 1 ? (
                <div className="flex flex-col gap-1">
                  <h3 className="txt-corpo font-semibold text-tinta">Saldos informados antes</h3>
                  <ul className="txt-apoio flex flex-col gap-1 text-tinta-secundaria">
                    {c.saldos.slice(1, 5).map((s) => <li key={s.id}>{`${formatarDia(s.data)} · ${reaisComSinal(s.valor)} · ${s.fonte}${s.informadoPor ? ` · ${s.informadoPor}` : ''}`}</li>)}
                  </ul>
                </div>
              ) : null}
            </section>
          ) : null}
        </>
      )}

      <JanelaDoSaldo aberta={acao === 'saldo'} aoFechar={aoFecharAcao} />
    </>
  );
}

/** Hoje no fuso da operação (`diaDaOperacao`), e não no fuso do aparelho. */
function hojeIso(): string {
  return diaDaOperacao();
}

function JanelaDoSaldo({ aberta, aoFechar }: { readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [data, setData] = useState(hojeIso);
  const [valor, setValor] = useState('');
  const [fonte, setFonte] = useState('');
  const [tentou, setTentou] = useState(false);
  const negativo = valor.trim().startsWith('-');
  const absoluto = interpretarNumero(valor.trim().replace(/^-/, ''));
  const numero = absoluto === null ? null : negativo ? -absoluto : absoluto;
  const faltaData = !/^\d{4}-\d{2}-\d{2}$/.test(data) || data > hojeIso() ? 'Escolha a data do extrato, de hoje ou de antes.' : null;
  const faltaValor = numero === null ? 'Escreva o saldo em reais, por exemplo 125.000,00; negativo com o sinal de menos.' : null;
  const faltaFonte = fonte.trim().length < 5 ? 'Escreva de onde vem o saldo, por exemplo "extrato do banco de 06/10/2026".' : null;
  const informar = useMutation({
    mutationFn: () => apiDoOrcamento.informarSaldo(data, numero ?? 0, fonte),
    meta: { retorno: retornoDe<{ data: string; valor: number; fonte: string; antes: { data: string; valor: number } | null }>((r) => ({ titulo: 'Saldo de caixa informado', ...detalheDoSaldo(r) })) },
    onSuccess: async () => {
      aoFechar();
      setValor('');
      setFonte('');
      await cliente.invalidateQueries({ queryKey: ['orcamento', 'caixa'] });
    },
  });
  return (
    <Janela
      titulo="Informar o saldo do banco"
      descricao="Não há integração bancária: o saldo vem do extrato, com a fonte. Vale o mais recente; corrigir é informar de novo."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={informar.isPending} onClick={() => { setTentou(true); if (!faltaData && !faltaValor && !faltaFonte) informar.mutate(); }}>
            {informar.isPending ? 'Informando…' : 'Informar saldo'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {informar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(informar.error).erro}. ${explicarFalha(informar.error).comoResolver}`}</p> : null}
        <CampoDeData rotulo="Data do saldo" valor={data} aoMudar={setData} max={hojeIso()} ajuda="A data do extrato, de hoje ou de antes." erro={tentou ? faltaData : null} />
        <CampoTexto rotulo="Saldo (R$)" valor={valor} aoMudar={setValor} placeholder="125.000,00" validar={() => (tentou ? faltaValor : null)} maxLength={20} />
        <CampoTexto rotulo="Fonte" valor={fonte} aoMudar={setFonte} placeholder="Extrato do banco de 06/10/2026" validar={() => (tentou ? faltaFonte : null)} maxLength={200} />
      </div>
    </Janela>
  );
}
